import { http } from '@/api/http'
import type { SettingDocument, SettingList } from '@/types/api'

type Options = { signal?: AbortSignal }

/**
 * Per-user UI settings: named JSON documents stored for the signed-in profile
 * and returned unchanged (GET/PUT/PATCH/DELETE /v1/me/settings/:key). No
 * permission is needed; only the caller's own documents are reachable.
 * `version` is the optimistic lock: a stale one answers 409 CONFLICT.
 */
export const settingsService = {
  async list(keys: readonly string[], options?: Options): Promise<Record<string, SettingDocument>> {
    const { data } = await http.get<SettingList>('/me/settings', { params: { keys: keys.join(',') }, ...options })
    return data.items ?? {}
  },

  async get(key: string, options?: Options): Promise<SettingDocument> {
    const { data } = await http.get<SettingDocument>(`/me/settings/${key}`, options)
    return data
  },

  /** Replaces the whole document; version 0 means "must not exist yet". */
  async put(key: string, value: unknown, version: number, options?: Options): Promise<SettingDocument> {
    const { data } = await http.put<SettingDocument>(`/me/settings/${key}`, { value, version }, options)
    return data
  },

  /** JSON Merge Patch (RFC 7396) on the stored value: members merge, null removes, arrays replace. */
  async patch(key: string, patch: Record<string, unknown>, version: number, options?: Options): Promise<SettingDocument> {
    const { data } = await http.patch<SettingDocument>(`/me/settings/${key}`, patch, {
      ...options,
      headers: { 'Content-Type': 'application/merge-patch+json', 'If-Match': `"${version}"` },
    })
    return data
  },

  /** Back to defaults; deleting a missing document is not an error. */
  async remove(key: string, options?: Options): Promise<void> {
    await http.delete(`/me/settings/${key}`, options)
  },
}
